import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./styles/Home.css";

const MENU_API = "https://salathai.co.tz/mrms/Menu.php";
const ORDER_API = "https://salathai.co.tz/mrms/Orders.php";
const PAYMENT_API = "https://salathai.co.tz/mrms/Payment.php";

export default function Home() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const staff = JSON.parse(localStorage.getItem("staff") || "{}");
  const [menu, setMenu] = useState([]);
  const [groups, setGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState("");
  const [cart, setCart] = useState([]);
  const [viewSide, setViewSide] = useState("order");
  const [orders, setOrders] = useState([]);
  const [darkMode, setDarkMode] = useState(false);
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [editingOrderNumber, setEditingOrderNumber] = useState(null);

  const cartModalRef = useRef(null);

  const isToday = (dateString) => {
    const orderDate = new Date(dateString);
    const today = new Date();
    return orderDate.toDateString() === today.toDateString();
  };

  useEffect(() => {
    fetch(MENU_API).then(res => res.json()).then(data => {
      setMenu(data);
      const uniqueGroups = [...new Set(data.map(i => i.group_button))];
      const sortedGroups = uniqueGroups.sort((a, b) => a.localeCompare(b));
      setGroups(sortedGroups);
      if (data.length > 0 && sortedGroups.length > 0) {
        setActiveGroup(sortedGroups[0]);
      }
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cartModalRef.current && !cartModalRef.current.contains(event.target)) {
        setCartModalOpen(false);
      }
    };

    if (cartModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [cartModalOpen]);

  useEffect(() => {
    if (viewSide === "payment") {
      loadMyPendingOrders();
      const interval = setInterval(loadMyPendingOrders, 3000);
      return () => clearInterval(interval);
    }
  }, [viewSide]);

  const loadMyPendingOrders = async () => {
    try {
      const res = await fetch(`${ORDER_API}?staff_id=${staff.id}&token=${token}`);
      const data = await res.json();
      if (Array.isArray(data)) {
        // Show READY orders that are UNPAID and from today
        const todayPendingOrders = data.filter(order => 
          order.status === "READY" && 
          order.payment_status === "UNPAID" &&
          isToday(order.created_at)
        );
        setOrders(todayPendingOrders);
      }
    } catch (err) {
      console.error("Load pending orders error:", err);
    }
  };

  const addToCart = (item) => {
    setCart(cart => {
      const exist = cart.find(c => c.id === item.id);
      if (exist) return cart.map(c => c.id === item.id ? { ...c, qty: c.qty + 1 } : c);
      return [...cart, { ...item, qty: 1 }];
    });
  };

  const removeFromCart = (id) => setCart(cart.filter(c => c.id !== id));
  
  const decreaseQty = (id) => {
    setCart(cart => {
      const item = cart.find(c => c.id === id);
      if (item.qty > 1) {
        return cart.map(c => c.id === id ? { ...c, qty: c.qty - 1 } : c);
      } else {
        return cart.filter(c => c.id !== id);
      }
    });
  };

  const cartTotal = cart.reduce((sum, i) => sum + i.price_tzs * i.qty, 0);
  const cartItemCount = cart.reduce((sum, i) => sum + i.qty, 0);

  const printReceipt = (orderNumber) => {
    const total = cartTotal;
    const now = new Date();
    const date = now.toLocaleDateString('en-GB');
    const time = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    const day = now.toLocaleDateString('en-US', { weekday: 'long' });

    const itemsHTML = cart.map(i => `
      <tr>
        <td style="padding: 6px 0; font-size: 13px;">${i.item_name}</td>
        <td style="padding: 6px 0; text-align: center;">${i.qty}</td>
        <td style="padding: 6px 0; text-align: right;">${(i.price_tzs * i.qty).toLocaleString()}</td>
      </tr>
    `).join("");

    const receiptHTML = `
      <!DOCTYPE html><html><head><title>Receipt</title><style>
      *{margin:0;padding:0;box-sizing:border-box}
      body{font-family:'Segoe UI',Arial,sans-serif;padding:20px;max-width:380px;margin:0 auto;background:#f5f5f5}
      .receipt{background:white;border-radius:12px;padding:20px;box-shadow:0 4px 12px rgba(0,0,0,0.1)}
      .header{text-align:center;margin-bottom:20px}
      .name{color:#2d6187;font-size:22px;font-weight:700;margin:5px 0}
      .address{font-size:11px;color:#666;margin:3px 0;line-height:1.4}
      hr{border:none;border-top:2px dashed #079264;margin:15px 0}
      table{width:100%;border-collapse:collapse;margin:12px 0}
      th{text-align:left;padding:8px 0;border-bottom:2px solid #2dda27;color:#2d6187;font-size:13px}
      td{padding:6px 0;font-size:12px}
      .total-row{font-size:18px;font-weight:800;text-align:right;margin-top:12px;padding-top:8px;border-top:2px solid #2d6187}
      .info-grid{display:flex;flex-wrap:wrap;justify-content:space-between;font-size:12px;margin:10px 0;background:#f8f9fa;padding:8px;border-radius:6px}
      .info-item{margin:3px 0}
      @media print{body{background:white;padding:0}.receipt{box-shadow:none;padding:15px}}
      </style></head>
      <body>
        <div class="receipt">
          <div class="header">
            <div class="name">SALATHAI RESTAURANT</div>
            <div class="address">Masaki</div>
            <div class="address">Dar es Salaam, Tanzania</div>
            <div class="address">TEL: +255 745 828 574</div>
          </div>
          <hr/>
          <div><strong>RECEIPT:</strong> ${orderNumber}</div>
          <div class="info-grid">
            <div class="info-item"><strong>Date:</strong> ${date}</div>
            <div class="info-item"><strong>Time:</strong> ${time}</div>
            <div class="info-item"><strong>Day:</strong> ${day}</div>
          </div>
          <div><strong>Waiter:</strong> ${staff.full_name}</div>
          <table>
            <thead><tr><th>ITEM</th><th>QTY</th><th>AMOUNT</th></tr></thead>
            <tbody>${itemsHTML}</tbody>
          </table>
          <div class="total-row"><strong>TOTAL:</strong> ${total.toLocaleString()} TZS</div>
        </div>
        <script>
          window.onload = function() { window.print(); setTimeout(() => window.close(), 500); };
        </script>
      </body></html>`;

    const printWindow = window.open('', '_blank', 'width=400,height=600');
    printWindow.document.write(receiptHTML);
    printWindow.document.close();
    printWindow.focus();
  };

  const createOrder = async () => {
    if (!cart.length) {
      Swal.fire("Cart Empty", "Please add items to cart", "warning");
      return;
    }
    
    if (isSubmitting) {
      Swal.fire("Processing", "Please wait, your order is being processed...", "info");
      return;
    }
    
    setIsSubmitting(true);
    
    if (window.innerWidth < 1024) {
      setCartModalOpen(false);
    }

    const items = cart.map(c => ({ 
      menu_id: c.id, 
      quantity: c.qty,
      price: c.price_tzs
    }));
    
    const idempotencyKey = `${staff.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    try {
      const body = { 
        items,
        staff_id: staff.id,
        idempotency_key: idempotencyKey
      };

      // If editing an existing order, include the update_order_id
      if (editingOrderId) {
        body.update_order_id = editingOrderId;
      }

      const res = await fetch(`${ORDER_API}?action=create&token=${token}`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "X-Idempotency-Key": idempotencyKey
        },
        body: JSON.stringify(body)
      });

      const data = await res.json();
      
      if (data.order_number) {
        if (data.already_processed) {
          Swal.fire("Info", `Order #${data.order_number} was already processed`, "info");
        } else if (data.updated) {
          printReceipt(data.order_number);
          Swal.fire({
            title: "Order Updated!",
            html: `Order #<strong>${data.order_number}</strong> has been updated with new items.<br/>New Total: <strong>${Number(data.total_amount).toLocaleString()} TZS</strong>`,
            icon: "success"
          });
        } else {
          printReceipt(data.order_number);
          Swal.fire("Success", `Order #${data.order_number} created successfully! Ready for payment.`, "success");
        }
        setCart([]);
        setCartModalOpen(false);
        setEditingOrderId(null);
        setEditingOrderNumber(null);
        loadMyPendingOrders();
      } else {
        Swal.fire("Error", data.error || "Failed to create order", "error");
      }
    } catch (err) {
      Swal.fire("Error", "Network error occurred. Please check your connection.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add items to existing order
  const handleAddToExistingOrder = (order) => {
    setEditingOrderId(order.id);
    setEditingOrderNumber(order.order_number);
    setCart([]); // Clear cart for new items
    setViewSide("order");
    setCartModalOpen(false);
    
    Swal.fire({
      title: "Add Items to Order",
      html: `Adding items to order <strong>#${order.order_number}</strong><br/><small>Select items from the menu and click "Place Order" to add them.</small>`,
      icon: "info",
      timer: 3000,
      showConfirmButton: false,
      toast: true,
      position: 'top-end'
    });
  };

  const payOrder = async (order) => {
    if (order.status !== "READY") {
      Swal.fire("Not Ready", "Order must be READY before payment", "warning");
      return;
    }

    const { value: method } = await Swal.fire({
      title: "Payment Method",
      input: "select",
      inputOptions: { 
        CASH: "CASH", 
        "M-PESA": "M-PESA", 
        "MIX BY YAS": "MIX BY YAS", 
        SELECOM: "SELECOM", 
        "CRDB BANK": "CRDB BANK" 
      },
      inputPlaceholder: "Select payment method",
      showCancelButton: true
    });
    
    if (!method) return;
    
    try {
      const res = await fetch(`${PAYMENT_API}?action=create&token=${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          order_id: order.id, 
          payment_method: method 
        })
      });
      
      const data = await res.json();
      if (data.message) {
        Swal.fire({
          title: "Submitted!",
          text: "Payment pending approval by cashier",
          icon: "success",
          timer: 1500,
          showConfirmButton: false
        });
        loadMyPendingOrders();
      } else {
        Swal.fire("Error", data.error || "Payment failed", "error");
      }
    } catch (err) {
      Swal.fire("Error", "Network error", "error");
    }
  };

  const handleGoToHomepage = () => navigate("/");

  const formatOrderTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true
    });
  };

  const formatOrderDate = (dateString) => {
    return new Date(dateString).toLocaleDateString([], {
      month: 'short',
      day: 'numeric'
    });
  };

  const createOrderButtonStyles = {
    background: "linear-gradient(135deg, #2bdba6 0%, #1dd186 100%)",
    color: "white",
    border: "none",
    padding: "14px 28px",
    fontSize: "16px",
    fontWeight: "600",
    borderRadius: "12px",
    cursor: "pointer",
    marginTop: "24px",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: "0 4px 15px rgba(9, 194, 86, 0.4)",
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
    letterSpacing: "0.5px"
  };

  return (
    <div className={`mrms-home-container ${darkMode ? "mrms-dark-theme" : "mrms-light-theme"}`}>
      <header className="mrms-home-header">
        <h2>SALATHAI RESTAURANT</h2>
        <div className="mrms-home-staff">
          <div className="mrms-user-profile" onClick={handleGoToHomepage}>
            <span className="mrms-user-icon">👤</span>
            <span className="mrms-user-name">{staff.full_name}</span>
          </div>
          <button className="mrms-home-mode-btn" onClick={() => setDarkMode(!darkMode)}>
            {darkMode ? "☀ Light" : "🌙 Dark"}
          </button>
        </div>
      </header>

      {/* Editing Order Banner */}
      {editingOrderId && viewSide === "order" && (
        <div style={{
          background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
          color: "white",
          padding: "12px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "10px"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontSize: "20px" }}>✏️</span>
            <span>
              Adding items to Order <strong>#{editingOrderNumber}</strong>
            </span>
          </div>
          <button
            onClick={() => {
              setEditingOrderId(null);
              setEditingOrderNumber(null);
              setCart([]);
            }}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "1px solid white",
              color: "white",
              padding: "6px 16px",
              borderRadius: "20px",
              cursor: "pointer",
              fontWeight: "600"
            }}
          >
            Cancel Edit
          </button>
        </div>
      )}

      <nav className="mrms-home-nav">
        <button 
          className={viewSide === "order" ? "active" : ""}
          onClick={() => {
            setViewSide("order");
            setCartModalOpen(false);
          }}
        >
          Make Order
        </button>
        <button 
          className={viewSide === "payment" ? "active" : ""}
          onClick={() => { 
            setViewSide("payment"); 
            loadMyPendingOrders(); 
            setCartModalOpen(false);
          }}
        >
          Pending Payments <span className="nav-badge">{orders.length}</span>
        </button>
      </nav>

      <main className="mrms-home-main">
        {viewSide === "order" ? (
          <div className="mrms-order-layout">
            <section className="mrms-order-section">
              <div className="mrms-group-buttons">
                {groups.map(g => (
                  <button 
                    key={g} 
                    onClick={() => setActiveGroup(g)}
                    className={activeGroup === g ? "active" : ""}
                  >
                    {g}
                  </button>
                ))}
              </div>
              
              {!activeGroup ? (
                <div className="mrms-no-category">
                  <div className="mrms-no-category-icon"></div>
                  <h3>Select a Category</h3>
                  <p>Choose a category from above to view menu items</p>
                </div>
              ) : (
                <div className="mrms-menu-container">
                  <div className="mrms-menu-grid">
                    {menu
                      .filter(i => i.group_button === activeGroup)
                      .map(item => (
                        <div key={item.id} className="mrms-menu-card">
                          <button 
                            className="mrms-menu-item-button"
                            onClick={() => addToCart(item)}
                          >
                            <h4 className="mrms-item-name">{item.item_name}</h4>
                            <p className="mrms-menu-price">{item.price_tzs.toLocaleString()} TZS</p>
                            <span className="mrms-add-btn">+ Add</span>
                          </button>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </section>

            {cart.length > 0 && (
              <aside className="mrms-cart-sidebar">
                <div className="mrms-cart-header">
                  <h3>🛒 {editingOrderId ? "New Items" : "Your Order"}</h3>
                  <span className="mrms-cart-count">({cartItemCount} items)</span>
                </div>
                <div className="mrms-cart-items">
                  {cart.map(item => (
                    <div key={item.id} className="mrms-cart-item">
                      <div className="mrms-cart-item-info">
                        <span className="mrms-cart-item-name">{item.item_name}</span>
                        <span className="mrms-cart-item-price">{(item.price_tzs * item.qty).toLocaleString()} TZS</span>
                      </div>
                      <div className="mrms-cart-item-controls">
                        <button className="mrms-cart-btn-decrease" onClick={() => decreaseQty(item.id)}>-</button>
                        <span className="mrms-cart-item-qty">{item.qty}</span>
                        <button className="mrms-cart-btn-increase" onClick={() => addToCart(item)}>+</button>
                        <button className="mrms-cart-btn-remove" onClick={() => removeFromCart(item.id)}>×</button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mrms-cart-summary">
                  <div className="mrms-cart-subtotal">
                    <span>Subtotal:</span>
                    <span>{cartTotal.toLocaleString()} TZS</span>
                  </div>
                  <button 
                    className="mrms-cart-checkout-btn" 
                    onClick={createOrder}
                    disabled={isSubmitting}
                    style={editingOrderId ? { background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)" } : {}}
                  >
                    {isSubmitting ? "⏳ Processing..." : editingOrderId ? "✏️ Update Order" : "🚀 Place Order"}
                  </button>
                </div>
              </aside>
            )}
          </div>
        ) : (
          <section className="mrms-payment-section">
            <div className="mrms-orders-table-wrapper">
              <div className="today-info-banner">
                <div className="today-info-content">
                  <span className="today-icon"></span>
                  <span className="today-text">
                    Showing pending orders for <strong>{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</strong>
                  </span>
                </div>
                <div className="order-count-badge">
                  {orders.length} Order{orders.length !== 1 ? 's' : ''}
                </div>
              </div>

              {orders.length === 0 ? (
                <div className="mrms-no-orders">
                  <div className="mrms-no-orders-icon">📭</div>
                  <h3>No Pending Orders Today</h3>
                  <p>You have no orders ready for payment from today's date.</p>
                  <button 
                    className="mrms-create-order-btn"
                    onClick={() => setViewSide("order")}
                    style={createOrderButtonStyles}
                  >
                    <span>+</span> Create New Order
                  </button>
                </div>
              ) : (
                <div className="mrms-table-responsive">
                  <table className="mrms-orders-table">
                    <thead>
                      <tr>
                        <th>Order #</th>
                        <th>Total</th>
                        <th>Status</th>
                        <th>Payment</th>
                        <th>Time</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map(o => (
                        <tr key={o.id}>
                          <td data-label="Order #">
                            <div className="mrms-order-number">{o.order_number}</div>
                            <div className="mrms-order-date">{formatOrderDate(o.created_at)}</div>
                          </td>
                          <td data-label="Total" className="mrms-order-total">
                            {Number(o.total_amount).toLocaleString()} TZS
                          </td>
                          <td data-label="Status">
                            <span className={`mrms-status ${o.status.toLowerCase()}`}>
                              {o.status}
                            </span>
                          </td>
                          <td data-label="Payment">
                            <span className={`mrms-payment-status ${o.payment_status.toLowerCase()}`}>
                              {o.payment_status}
                            </span>
                          </td>
                          <td data-label="Time" className="mrms-order-time-cell">
                            {formatOrderTime(o.created_at)}
                          </td>
                          <td data-label="Actions">
                            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                              {/* ADD ITEMS BUTTON */}
                              <button
                                onClick={() => handleAddToExistingOrder(o)}
                                style={{
                                  background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                                  color: "white",
                                  border: "none",
                                  padding: "8px 14px",
                                  borderRadius: "8px",
                                  fontSize: "13px",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px"
                                }}
                              >
                                ➕ Add Items
                              </button>
                              
                              {/* PAY NOW BUTTON */}
                              <button 
                                disabled={o.payment_status === "PAID" || o.status !== "READY"} 
                                onClick={() => payOrder(o)}
                                className={o.payment_status === "PAID" ? "mrms-paid-btn" : "mrms-pay-btn"}
                              >   
                                {o.payment_status === "PAID" ? "✅ Paid" : o.status === "READY" ? "Pay Now" : "Not Ready"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        )}
      </main>

      {viewSide === "order" && (
        <div className="mrms-cart-floating">
          {cart.length > 0 && (
            <div className="mrms-cart-floating-total">
              {cartTotal.toLocaleString()} TZS
            </div>
          )}
          <button 
            className="mrms-cart-floating-btn"
            onClick={() => setCartModalOpen(true)}
            disabled={isSubmitting}
          >
            🛒
            {cartItemCount > 0 && (
              <span className="mrms-cart-floating-count">{cartItemCount}</span>
            )}
          </button>
        </div>
      )}

      <div className={`mrms-cart-modal ${cartModalOpen ? 'active' : ''}`}>
        <div className="mrms-cart-modal-content" ref={cartModalRef}>
          <div className="mrms-cart-modal-header">
            <h3>{editingOrderId ? "New Items for Order" : `Your Order (${cartItemCount} items)`}</h3>
            <button 
              className="mrms-cart-modal-close"
              onClick={() => setCartModalOpen(false)}
            >
              ×
            </button>
          </div>
          
          {cart.length === 0 ? (
            <div className="mrms-cart-empty">
              <div className="mrms-cart-empty-icon">🛒</div>
              <h3>Your cart is empty</h3>
              <p>Add items from the menu to get started</p>
              <button 
                className="mrms-cart-empty-btn"
                onClick={() => {
                  setCartModalOpen(false);
                  if (groups.length > 0) {
                    setActiveGroup(groups[0]);
                  }
                }}
              >
                Browse Menu
              </button>
            </div>
          ) : (
            <>
              <div className="mrms-cart-modal-items">
                {cart.map(item => (
                  <div key={item.id} className="mrms-cart-modal-item">
                    <div className="mrms-cart-modal-item-info">
                      <span className="mrms-cart-modal-item-name">{item.item_name}</span>
                      <span className="mrms-cart-modal-item-price">
                        {(item.price_tzs * item.qty).toLocaleString()} TZS
                      </span>
                    </div>
                    <div className="mrms-cart-modal-item-controls">
                      <button 
                        className="mrms-cart-modal-btn decrease"
                        onClick={() => decreaseQty(item.id)}
                      >
                        -
                      </button>
                      <span className="mrms-cart-modal-qty">{item.qty}</span>
                      <button 
                        className="mrms-cart-modal-btn increase"
                        onClick={() => addToCart(item)}
                      >
                        +
                      </button>
                      <button 
                        className="mrms-cart-modal-btn remove"
                        onClick={() => removeFromCart(item.id)}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mrms-cart-modal-footer">
                <div className="mrms-cart-modal-total">
                  <span>Total:</span>
                  <span>{cartTotal.toLocaleString()} TZS</span>
                </div>
                <button 
                  className="mrms-cart-modal-checkout"
                  onClick={createOrder}
                  disabled={isSubmitting}
                  style={editingOrderId ? { background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)" } : {}}
                >
                  {isSubmitting ? "⏳ Processing..." : editingOrderId ? "✏️ Update Order" : "🚀 Place Order"}
                </button>
              </div>
            </>  
          )}
        </div>
      </div>
    </div>
  );
}